import { vhmanalytics } from "@src/proto/jsonapi";
import errorNotifier from "@src/utils/errorNotifierOther";
import { jsonFetch } from "@src/utils/fetch";
import { getIsHomePage } from "@src/utils/helper";

let cache: vhmanalytics.IAnalytics;
export function fetchAnalyticData() {
  if (cache) return Promise.resolve(cache);
  return jsonFetch<vhmanalytics.IAnalytics>(`/api/analytics.json`).then(
    (data = {}) => {
      cache = data;
      return data;
    }
  );
}

// prefetch analyticData in homepage to reduce the demand of network when triggering search event.
if (getIsHomePage()) {
  window.setTimeout(fetchAnalyticData, 100);
}

interface IParam {
  properties?: string;
  regions?: string;
  prefectures?: string;
  cities?: string;
  districts?: string;
  lines?: string;
  stations?: string;
}

export interface IReturn {
  properties?: vhmanalytics.IProperty;
  regions?: vhmanalytics.IRegion;
  prefectures?: vhmanalytics.IPrefecture;
  cities?: vhmanalytics.ICity;
  districts?: string;
  lines?: vhmanalytics.ILine;
  stations?: vhmanalytics.IStation;
}

export async function getAnalyticDataById(
  param: IParam,
  option?: { fullStation: boolean }
): Promise<IReturn> {
  const { fullStation = false } = option || {};
  const result: IReturn = {};
  let analyticJson: vhmanalytics.IAnalytics;
  try {
    analyticJson = await fetchAnalyticData();
  } catch (error) {
    if (error instanceof Error) {
      errorNotifier.notify({
        error,
        message: "fetchAnalyticData failed",
      });
    }

    return result;
  }

  function getData(config: IParam) {
    if (config.properties) {
      const property = analyticJson.properties
        ? analyticJson.properties[config.properties]
        : undefined;
      if (property) {
        config.cities = property.cid ?? undefined;
        config.prefectures = property.pid ?? undefined;
      }
    }
    if (config.stations && fullStation === true) {
      const stationItem = analyticJson.stations
        ? analyticJson.stations[config.stations]
        : undefined;
      if (stationItem) {
        config.cities = stationItem.cid ?? undefined;
        config.prefectures = stationItem.pid ?? undefined;
      }
    }
    Object.entries(config).forEach((entry) => {
      const [key, value] = entry;
      if (!analyticJson || !(key in analyticJson)) return;
      if (!value) return;
      const map = analyticJson[key as keyof vhmanalytics.IAnalytics];
      // @ts-expect-error ERROR TEST
      const item = map[value];
      if (key === "districts") {
        result.districts = item?.name ?? "";
        return;
      }
      if (key === "cities") {
        // 1. has parent city
        if (item.pCid) {
          // @ts-expect-error ERROR TEST
          result.districts = (map[item.pCid] as vhmanalytics.ICity).name ?? "";
          // 2. is other city
        } else if (item.oArea) {
          result.districts = analyticJson.vars?.oArea ?? "";
          // 3. the city item is district
        } else if (item.pCid === undefined) {
          result.districts = item?.name ?? "";
          return;
        }
      }
      result[key as keyof IReturn] = item;
    });
    return result;
  }

  return getData(param);
}

export function getAnalyticDataFromBreadcrumb(
  isFull?: boolean
): Promise<IReturn[] | IReturn> {
  const list = document.querySelector<HTMLOListElement>(
    ".container-breadcrumb .container-breadcrumb-list"
  );
  const children = list?.children;
  const length = children?.length ?? 0;
  const idParam: PlainObject<string> = {};
  const idParamList: PlainObject<string>[] = [];

  for (let i = 0; i < length; i++) {
    const child = children?.item(i) as HTMLLIElement;
    const type = child.dataset.resourceType ?? "";
    const id = child.dataset.resourceId ?? "";
    if (idParam[type]) {
      idParamList.push({ ...idParam });
    }
    idParam[type] = id;
  }

  if (isFull === true) {
    idParamList.push(idParam);
    return Promise.all(
      idParamList.map((item) =>
        getAnalyticDataById(
          {
            regions: item.region,
            prefectures: item.prefecture,
            cities: item.city,
            lines: item.line,
            stations: item.station,
          },
          { fullStation: true }
        )
      )
    );
  } else {
    return getAnalyticDataById({
      regions: idParam.region,
      prefectures: idParam.prefecture,
      cities: idParam.city,
      lines: idParam.line,
      stations: idParam.station,
    });
  }
}
